export default {
  index: {
    title: 'Guias de marés e correntes',
    description:
      'Aprenda como funcionam as marés e as correntes de maré: coeficientes de maré, correntes de enchente e vazante, referências de nível de maré (data), e como as previsões de maré são calculadas.',
    subtitle:
      'Explicações curtas e em linguagem simples sobre a ciência por trás das previsões de marés e correntes.',
  },
  back: 'Voltar a todos os guias',
  guides: {
    'tidal-coefficients': {
      title: 'Os coeficientes de maré explicados',
      description:
        'O que significam os coeficientes de maré: a escala de 20 a 120, como as marés de sizígia e de quadratura se relacionam com a Lua, e por que um coeficiente mais alto produz uma amplitude de maré maior.',
      blurb:
        'A escala de 20 a 120 que resume o quão grande será uma maré e como a Lua a impulsiona.',
      body: [
        {
          p: 'Um coeficiente de maré é um único número que resume o quão grande será uma maré em um determinado dia. É amplamente utilizado na França e em outros países que seguem a tradição hidrográfica francesa. O coeficiente é adimensional e normalmente varia de cerca de 20 a 120: quanto maior o número, maior a amplitude entre a preia-mar e a baixa-mar, e mais fortes são as correntes de maré associadas.',
        },
        { h2: 'A escala de 20 a 120' },
        {
          p: 'O coeficiente compara a amplitude de maré do dia com uma referência de longo prazo. Vários pontos da escala têm significados convencionais:',
        },
        {
          list: [
            { term: '20', text: 'as menores marés, próximas da amplitude astronômica mínima.' },
            { term: '45', text: 'uma maré de quadratura média, quando a amplitude é pequena.' },
            {
              term: '70',
              text: 'uma maré média, aproximadamente no ponto intermediário entre as quadraturas e as sizígias.',
            },
            { term: '95', text: 'uma maré de sizígia média, quando a amplitude é grande.' },
            { term: '120', text: 'as maiores marés, próximas da amplitude astronômica máxima.' },
          ],
        },
        { h2: 'Por que muda: a Lua' },
        {
          p: 'Os coeficientes sobem e descem segundo o ciclo de sizígia-quadratura, de cerca de duas semanas. As marés de sizígia, com coeficientes altos, ocorrem um ou dois dias após cada Lua nova e cheia, quando o Sol e a Lua se alinham e suas forças de maré se somam. As marés de quadratura, com coeficientes baixos, ocorrem perto do quarto crescente e do quarto minguante, quando o Sol e a Lua formam um ângulo reto e se cancelam em parte.',
        },
        {
          p: 'A distância da Lua também importa. Quando uma maré de sizígia coincide com o perigeu (o ponto de maior aproximação da Lua à Terra), o coeficiente atinge seus valores mais altos. As maiores marés tendem a se concentrar perto dos equinócios de primavera e de outono.',
        },
        { h2: 'O que um coeficiente não diz' },
        {
          p: 'O coeficiente descreve apenas a amplitude astronômica. Não inclui o efeito do tempo meteorológico: o vento e a pressão atmosférica podem elevar ou baixar o nível real da água independentemente do coeficiente. É também uma propriedade da maré como um todo para aquele dia, não uma altura em metros ou pés em um único local.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Enchente, vazante e estofo: como funcionam as correntes de maré',
      description:
        'Como funcionam as correntes de maré: a direção da enchente e da vazante, o estofo no momento da inversão, a velocidade da corrente em nós e sua direção (o set), e por que o estofo nem sempre coincide com a preia-mar e a baixa-mar.',
      blurb:
        'A enchente, a vazante e o estofo, e por que a corrente mais forte nem sempre ocorre na preia-mar.',
      body: [
        {
          p: 'As correntes de maré são o movimento horizontal da água que acompanha a subida e a descida verticais da maré. Enquanto a altura da maré indica a profundidade da água, a corrente indica em que direção ela flui e com que velocidade. As correntes importam sobretudo em canais, embocaduras e estuários, onde podem atingir vários nós.',
        },
        { h2: 'Enchente, vazante e estofo' },
        {
          list: [
            {
              term: 'Corrente de enchente',
              text: 'água fluindo em direção à costa ou subindo um estuário, geralmente enquanto a maré sobe.',
            },
            {
              term: 'Corrente de vazante',
              text: 'água fluindo em direção ao mar ou descendo um estuário, geralmente enquanto a maré desce.',
            },
            {
              term: 'Estofo',
              text: 'a breve pausa em que a corrente é quase nula ao inverter entre a enchente e a vazante.',
            },
          ],
        },
        {
          p: 'A velocidade da corrente é medida em nós (milhas náuticas por hora). Sua direção, chamada de set, é dada como um rumo da bússola: a direção para a qual a água flui.',
        },
        { h2: 'O estofo nem sempre ocorre na preia-mar' },
        {
          p: 'Costuma-se supor que a corrente para na preia-mar e na baixa-mar. Isso só é verdade em alguns lugares. O momento depende de como a onda de maré se comporta localmente. Onde a maré se comporta como uma onda progressiva, como em um longo canal aberto, a enchente e a vazante mais fortes ocorrem perto da preia-mar e da baixa-mar, e o estofo cai perto do ponto médio. Onde ela se comporta como uma onda estacionária, como no fundo de uma baía fechada, ocorre o contrário: a corrente mais rápida se dá na meia-maré e o estofo ocorre perto da preia-mar e da baixa-mar. As tábuas de correntes locais são o guia confiável.',
        },
        { h2: 'Correntes alternadas e rotatórias' },
        {
          p: 'Em canais estreitos, a corrente costuma ser alternada: flui em um sentido na enchente, depois se inverte e volta na vazante. Em águas costeiras abertas, a corrente é muitas vezes rotatória: muda de direção continuamente por toda a bússola ao longo de um ciclo de maré, em vez de se inverter ao longo de uma única linha.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Referências de nível e alturas de maré',
      description:
        'A partir de que se mede uma altura de maré: o nível de redução, a mais baixa maré astronômica (LAT), a média das baixa-mares inferiores (MLLW), e por que as referências diferem entre os países.',
      blurb:
        'O nível de redução, LAT, MLLW, e o que o número de uma altura de maré realmente significa.',
      body: [
        {
          p: 'Toda altura de maré é uma medida acima de um nível de referência chamado datum. Quando uma tábua de marés diz que a água vai atingir 1.8 metro, significa 1.8 metro acima do nível de referência de maré local, não acima do fundo nem acima do nível do mar no sentido cotidiano. Conhecer o nível de referência é o que torna uma altura significativa.',
        },
        { h2: 'O que é um nível de referência de maré' },
        {
          p: 'Um nível de referência de maré é uma elevação de referência definida por um estágio específico da maré, calculada como média ao longo de um longo período: convencionalmente cerca de 19 anos, o que suaviza o ciclo de 18.6 anos da órbita da Lua. Por ser uma média de observações reais, uma referência é específica de um local e da autoridade que a calculou.',
        },
        { h2: 'O nível de redução e as referências comuns' },
        {
          p: 'As cartas náuticas usam um nível de referência de baixa-mar para que a profundidade real seja quase sempre pelo menos igual à profundidade indicada na carta. Esse nível de redução é o zero a partir do qual se contam tanto as profundidades cartografadas quanto as alturas de maré. Diferentes países adotam referências diferentes:',
        },
        {
          list: [
            {
              term: 'LAT (mais baixa maré astronômica)',
              text: 'o nível mais baixo previsível sob tempo meteorológico médio; usado pelo Reino Unido e por muitas outras nações.',
            },
            {
              term: 'MLLW (média das baixa-mares inferiores)',
              text: 'a média da mais baixa das duas baixa-mares de cada dia; o nível de redução padrão nos Estados Unidos.',
            },
            {
              term: 'MSL (nível médio do mar)',
              text: 'o nível médio da água a longo prazo, usado principalmente como referência para elevações do terreno, e não para cartas.',
            },
          ],
        },
        {
          p: 'As referências de preia-mar, como a preia-mar média (MHW) e a preia-mar superior média (MHHW), servem ao propósito oposto, por exemplo para definir uma linha de costa ou o vão livre de uma ponte.',
        },
        { h2: 'Por que as alturas não são diretamente comparáveis' },
        {
          p: 'Como as referências diferem, uma altura em uma estação não pode ser comparada diretamente com uma altura em outra a menos que ambas usem a mesma referência. Uma baixa-mar de 0.5 metro medida a partir de LAT não é o mesmo nível de água que uma baixa-mar de 0.5 metro medida a partir de MLLW. É também por isso que a mesma maré física pode ser descrita por números diferentes nas tábuas de diferentes países.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Como as previsões de maré são calculadas',
      description:
        'Como as marés são previstas: o forçamento gravitacional da Lua e do Sol, as componentes harmônicas como M2 e S2, e por que os níveis de água observados diferem da previsão astronômica.',
      blurb:
        'A análise harmônica, as componentes por trás da maré, e por que o tempo meteorológico faz a realidade divergir.',
      body: [
        {
          p: 'As marés podem ser previstas com anos de antecedência porque são impulsionadas pelos movimentos da Lua e do Sol, conhecidos com grande precisão. A maré em um lugar é a resposta do oceano a esse forçamento gravitacional, moldada pela costa e pela profundidade locais. O método que transforma a astronomia em uma tábua de marés chama-se análise harmônica.',
        },
        { h2: 'A maré como soma de componentes' },
        {
          p: 'A análise harmônica trata a maré como a soma de muitas ondas simples, cada uma chamada de componente. Cada componente tem uma frequência fixada pela astronomia (a forma como a Terra, a Lua e o Sol se movem umas em relação às outras) enquanto sua amplitude e sua fase são obtidas ajustando as componentes a um período de observações reais do nível da água naquele local. As principais componentes incluem:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'a componente semidiurna lunar principal, de período 12.42 horas; geralmente a maior.',
            },
            {
              term: 'S2',
              text: 'a componente semidiurna solar principal, de período 12.00 horas.',
            },
            { term: 'N2', text: 'uma componente lunar devida à órbita elíptica da Lua.' },
            {
              term: 'K1 e O1',
              text: 'as principais componentes diurnas, que fazem com que uma preia-mar do dia seja maior que a outra em muitos lugares.',
            },
          ],
        },
        {
          p: 'O familiar ciclo de sizígia-quadratura surge naturalmente disso: M2 e S2 têm períodos ligeiramente diferentes, de modo que entram e saem de fase, reforçando-se a cada 14.8 dias para produzir as sizígias e cancelando-se em parte para produzir as quadraturas.',
        },
        { h2: 'Prever adiante' },
        {
          p: 'Uma vez conhecidas a amplitude e a fase de cada componente para um local, prever a maré é simplesmente uma questão de avaliar a soma em qualquer instante futuro. As frequências astronômicas nunca mudam, de modo que o cálculo é determinístico e pode ser executado com décadas de antecedência.',
        },
        { h2: 'Por que a maré real difere' },
        {
          p: 'Uma previsão é apenas a maré astronômica. Não inclui o tempo meteorológico. Ventos fortes, pressão atmosférica baixa ou alta, chuvas intensas e a vazão dos rios podem elevar ou baixar o nível real da água: uma maré de tempestade pode acrescentar um metro ou mais acima da previsão, enquanto a alta pressão e os ventos de terra podem manter a água bem abaixo dela. As previsões são, portanto, um excelente guia para o momento e o tamanho da maré, mas o nível observado no dia pode diferir.',
        },
      ],
    },
  },
}
